import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon with an embedded SVG counter.
 */
class CartIcon extends Component {
  // We no longer require the old bubble refs
  requiredRefs = [];
  
  // Internal reference to the SVG text element
  svgTextElement = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Find our new SVG text element
    this.svgTextElement = this.querySelector('#cart-icon-text');
    
    // Check for initial count from liquid render
    const initialCountEl = this.querySelector('[data-cart-count-initial]');
    if (initialCountEl && this.svgTextElement) {
       this.updateCount(parseInt(initialCountEl.textContent, 10), false);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    
    // Check session storage on load
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const currentCount = parseInt(this.svgTextElement?.textContent || '0', 10);
    const source = event.detail.data?.source;
    
    // If coming from product form (Ajax add), we add to current. 
    // If coming from cart drawer/page update, we replace the total.
    let newCount = itemCount;
    if (source === 'product-form-component') {
        newCount = currentCount + itemCount;
    }

    this.updateCount(newCount, true);
  };

  /**
   * Updates the SVG text and handles animation
   */
  updateCount(count, animate = true) {
    if (!this.svgTextElement) return;

    // 1. Update the text content
    // If count is greater than 99, show 99+ to fit in the SVG
    this.svgTextElement.textContent = count > 99 ? '99+' : count;

    // 2. Set attribute for CSS (to hide if 0)
    this.svgTextElement.setAttribute('data-count', count);

    // 3. Update parent class for styling hooks
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    // 4. Update Session Storage
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );

    // 5. Run Animation
    if (animate && count > 0) {
      // Remove class if it exists to restart animation
      this.svgTextElement.classList.remove('bump');
      
      // Trigger reflow
      void this.svgTextElement.offsetWidth;
      
      // Add class back
      this.svgTextElement.classList.add('bump');
      
      // Remove after animation finishes (approx 200ms)
      setTimeout(() => {
        this.svgTextElement.classList.remove('bump');
      }, 200);
    }
  }

  /**
   * Checks session storage to keep count in sync across tabs/back buttons
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const currentVisible = this.svgTextElement?.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === currentVisible) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) {
          this.updateCount(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}